import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'
import { ApiError } from '@/lib/api-client'
import { useAuth } from '@/features/auth/auth-provider'
import {
  approveAuthorizeRequest,
  clientVendorDescription,
  denyAuthorizeRequest,
  fetchAuthorizeRequest,
  MCP_SCOPE_LABELS,
  scopeLabel,
  type ConsentWorkspace,
} from '@/features/settings/mcp-api'
import { ROLE_LABELS } from '@/pages/settings/team'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'

const REQUIRED_SCOPE = 'mindkeep:read'
const WRITE_SCOPE = 'mindkeep:inbox:write'

export function ConnectPage() {
  const [searchParams] = useSearchParams()
  const requestId = searchParams.get('request') ?? ''
  const { isAuthenticated, isLoading: authLoading } = useAuth()

  const requestQuery = useQuery({
    queryKey: ['mcp-authorize-request', requestId],
    queryFn: () => fetchAuthorizeRequest(requestId),
    enabled: isAuthenticated && requestId.length > 0,
    retry: false,
  })

  const data = requestQuery.data
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string | null>(null)
  const [selectedScopes, setSelectedScopes] = useState<string[]>([])
  const [redirecting, setRedirecting] = useState(false)

  const workspaces = useMemo(() => data?.workspaces ?? [], [data])
  const effectiveWorkspaceId = selectedWorkspaceId ?? workspaces[0]?.id ?? null
  const selectedWorkspace: ConsentWorkspace | undefined = workspaces.find((w) => w.id === effectiveWorkspaceId)

  const grantableScopes = useMemo(() => {
    if (!data || !selectedWorkspace) return []
    return data.request.requested_scopes.filter((scope) => selectedWorkspace.grantable_scopes.includes(scope))
  }, [data, selectedWorkspace])

  // Reset the scope selection whenever the workspace changes: every grantable
  // scope except the required read scope is checked by default.
  useEffect(() => {
    if (!data || !effectiveWorkspaceId) return
    const ws = data.workspaces.find((w) => w.id === effectiveWorkspaceId)
    if (!ws) return
    const grantable = data.request.requested_scopes.filter((scope) => ws.grantable_scopes.includes(scope))
    setSelectedScopes(grantable.filter((scope) => scope !== REQUIRED_SCOPE))
  }, [data, effectiveWorkspaceId])

  const checkedScopes = useMemo(() => {
    const checked = new Set(selectedScopes)
    if (grantableScopes.includes(REQUIRED_SCOPE)) checked.add(REQUIRED_SCOPE)
    return grantableScopes.filter((scope) => checked.has(scope))
  }, [grantableScopes, selectedScopes])

  const writeRequestedButNotGrantable =
    !!data && data.request.requested_scopes.includes(WRITE_SCOPE) && !grantableScopes.includes(WRITE_SCOPE)

  function toggleScope(scope: string) {
    setSelectedScopes((prev) => (prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope]))
  }

  function redirectTo(url: string) {
    // The backend only returns registered https or loopback http callbacks;
    // refuse anything else (e.g. javascript:) defensively.
    let target: URL
    try {
      target = new URL(url)
    } catch {
      toast.error('Ungültige Weiterleitung.')
      return
    }
    if (target.protocol !== 'https:' && target.protocol !== 'http:') {
      toast.error('Ungültige Weiterleitung.')
      return
    }
    setRedirecting(true)
    window.location.assign(target.href)
  }

  const approve = useMutation({
    mutationFn: (workspaceId: string) => approveAuthorizeRequest(requestId, workspaceId, checkedScopes),
    onSuccess: (result) => redirectTo(result.redirect_url),
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Zulassen fehlgeschlagen'),
  })

  const deny = useMutation({
    mutationFn: () => denyAuthorizeRequest(requestId),
    onSuccess: (result) => redirectTo(result.redirect_url),
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Ablehnen fehlgeschlagen'),
  })

  const notFound =
    requestQuery.error instanceof ApiError &&
    requestQuery.error.status === 404 &&
    requestQuery.error.code === 'request_not_found'

  return (
    <main className="flex min-h-screen items-center justify-center bg-background px-4 py-10">
      <div className="w-full max-w-[440px]">
        <div className="mb-8 text-center">
          <h1 className="text-[26px] font-[650] leading-tight tracking-tight text-foreground">MindKeep</h1>
          <p className="mt-1 text-sm text-muted-foreground">KI-Wissensbasis für dein Unternehmen</p>
        </div>

        <Card>
          {!requestId ? (
            <>
              <CardHeader>
                <CardTitle>Ungültiger Verbindungslink.</CardTitle>
                <CardDescription>Starte die Verbindung in der App erneut.</CardDescription>
              </CardHeader>
            </>
          ) : authLoading || (isAuthenticated && requestQuery.isLoading) ? (
            <>
              <CardHeader>
                <Skeleton className="h-6 w-48" />
                <Skeleton className="h-4 w-64" />
              </CardHeader>
              <CardContent className="space-y-3">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-10 w-full" />
              </CardContent>
            </>
          ) : !isAuthenticated ? (
            <>
              <CardHeader>
                <CardTitle>Anmelden, um fortzufahren</CardTitle>
                <CardDescription>Melde dich an, um die Verbindung zu prüfen.</CardDescription>
              </CardHeader>
              <CardContent>
                <Button asChild className="w-full">
                  <Link to={`/login?next=${encodeURIComponent(`/verbinden?request=${requestId}`)}`}>Zum Login</Link>
                </Button>
              </CardContent>
            </>
          ) : notFound ? (
            <>
              <CardHeader>
                <CardTitle>Diese Anfrage ist abgelaufen oder wurde bereits verwendet.</CardTitle>
                <CardDescription>Starte die Verbindung in der App erneut.</CardDescription>
              </CardHeader>
            </>
          ) : requestQuery.error ? (
            <>
              <CardHeader>
                <CardTitle>Die Anfrage konnte nicht geladen werden.</CardTitle>
                <CardDescription>
                  {requestQuery.error instanceof Error ? requestQuery.error.message : 'Bitte versuche es später erneut.'}
                </CardDescription>
              </CardHeader>
            </>
          ) : data ? (
            <>
              <CardHeader className="pb-4">
                <CardTitle className="break-words">{data.request.client_name} mit MindKeep verbinden</CardTitle>
                <CardDescription>Weiterleitung an: {data.request.redirect_host}</CardDescription>
                <CardDescription>Anbieter: {clientVendorDescription(data.request.client_vendor)}</CardDescription>
                <p className="break-all font-mono text-xs text-muted-foreground">{data.request.client_id}</p>
              </CardHeader>
              <CardContent className="space-y-5">
                {workspaces.length > 0 ? (
                  <fieldset className="space-y-2">
                    <legend className="text-sm font-medium">Workspace</legend>
                    {workspaces.map((workspace) => (
                      <label
                        key={workspace.id}
                        className="flex cursor-pointer items-center gap-3 rounded-md border p-3 text-sm has-[:checked]:border-primary"
                      >
                        <input
                          type="radio"
                          name="workspace"
                          className="h-4 w-4 accent-primary"
                          checked={workspace.id === effectiveWorkspaceId}
                          onChange={() => setSelectedWorkspaceId(workspace.id)}
                        />
                        <span className="min-w-0">
                          <span className="block break-words font-medium">{workspace.name}</span>
                          <span className="block text-xs text-muted-foreground">{ROLE_LABELS[workspace.role]}</span>
                        </span>
                      </label>
                    ))}
                  </fieldset>
                ) : (
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Kein Workspace ist für Verbindungen freigegeben.</p>
                    {data.blocked_workspaces.some((w) => w.reason === 'provider_policy') ? (
                      <p className="text-xs text-muted-foreground">
                        Besitzer oder Admins können erlaubte Anbieter unter Einstellungen › Verbundene Apps ändern.
                      </p>
                    ) : null}
                  </div>
                )}

                {data.blocked_workspaces.length > 0 ? (
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-muted-foreground">Nicht verfügbar</p>
                    <ul className="space-y-1 text-xs text-muted-foreground">
                      {data.blocked_workspaces.map((workspace) => (
                        <li key={workspace.id}>
                          {workspace.name} –{' '}
                          {workspace.reason === 'provider_policy'
                            ? 'durch die Anbieter-Richtlinie gesperrt'
                            : 'Profil «Privat» oder «On-Premises», für externe Apps gesperrt'}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                {grantableScopes.length > 0 ? (
                  <fieldset className="space-y-2">
                    <legend className="text-sm font-medium">Zugriff</legend>
                    {grantableScopes.map((scope) => {
                      const meta = MCP_SCOPE_LABELS[scope]
                      const isRequired = scope === REQUIRED_SCOPE
                      const checked = isRequired || checkedScopes.includes(scope)
                      return (
                        <label
                          key={scope}
                          className="flex cursor-pointer items-start gap-3 rounded-md border p-3 text-sm has-[:checked]:border-primary"
                        >
                          <input
                            type="checkbox"
                            className="mt-0.5 h-4 w-4 accent-primary"
                            checked={checked}
                            disabled={isRequired}
                            onChange={() => toggleScope(scope)}
                          />
                          <span className="min-w-0">
                            <span className="block font-medium">{scopeLabel(scope)}</span>
                            {meta ? <span className="block text-xs text-muted-foreground">{meta.description}</span> : null}
                          </span>
                        </label>
                      )
                    })}
                    {writeRequestedButNotGrantable ? (
                      <p className="text-xs text-muted-foreground">
                        Speichern in die Inbox ist nur für Besitzer und Admins möglich.
                      </p>
                    ) : null}
                  </fieldset>
                ) : null}

                <div className="rounded-md border bg-muted/50 p-3 text-xs text-muted-foreground">
                  Inhalte, die die App über diese Verbindung liest, verlassen MindKeep und werden von{' '}
                  {data.request.client_name} verarbeitet. Du kannst die Verbindung jederzeit unter Einstellungen ›
                  Verbundene Apps widerrufen.
                  {data.request.client_location === 'usa' ? ' Der Anbieter sitzt ausserhalb der EU.' : ''}
                </div>

                <div className="flex gap-2">
                  <Button
                    type="button"
                    data-testid="connect-approve"
                    className="flex-1"
                    disabled={approve.isPending || deny.isPending || !effectiveWorkspaceId || redirecting}
                    onClick={() => effectiveWorkspaceId && approve.mutate(effectiveWorkspaceId)}
                  >
                    {redirecting ? 'Weiterleitung…' : approve.isPending ? 'Wird zugelassen…' : 'Zulassen'}
                  </Button>
                  <Button
                    type="button"
                    data-testid="connect-deny"
                    variant="outline"
                    className="flex-1"
                    disabled={approve.isPending || deny.isPending || redirecting}
                    onClick={() => deny.mutate()}
                  >
                    {redirecting ? 'Weiterleitung…' : deny.isPending ? 'Wird abgelehnt…' : 'Ablehnen'}
                  </Button>
                </div>
              </CardContent>
            </>
          ) : null}
        </Card>
      </div>
    </main>
  )
}
